import { TestRecordBanner } from "@src/common/TestRecordBanner";
import { ThemedIllustration } from "@src/common/ThemedIllustration";
import { LAYOUT_CONSTANTS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { FilterProvider } from "@src/context/FilterContext";
import { ProviderListProvider } from "@src/context/ProviderListContext";
import logger from "@src/utils/logger";
import { isTestRecordBlocked } from "@src/utils/test-record-utils";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { OverlayProvider } from "@uhg-abyss/web/ui/OverlayProvider";
import { Router } from "@uhg-abyss/web/ui/Router";
import { useEffect } from "react";
import { Filters } from "./Filters";
import { ProviderList } from "./ProviderList/ProviderList";

const PageLayout = styled("div", {
  maxWidth: `${LAYOUT_CONSTANTS.maxPageWidth} !important`,
  margin: "0 auto !important",
  width: "100%",
  minHeight: "100vh", // Ensure the layout takes up the full viewport height
  backgroundColor: "$app.color.default.background",
  position: "relative",
  display: "flex",
  flexDirection: "column",
});

const TopBGView = styled("div", {
  backgroundColor: "$app.color.top.background",
  width: "100%",
  minHeight: "235px",
  display: "flex",
  justifyContent: "center",
  position: "relative",
  zIndex: 1, // Lower z-index to place it behind DisplayView
  padding: "32px 52.97px",
  borderBottom: `1px solid $app.color.default.border`,
  variants: {
    isMobile: {
      true: {
        display: "none",
      },
    },
  },
});

const DisplayView = styled("div", {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: "776px",
  alignSelf: "center",
  justifySelf: "center !important",
  // left and transfrom in css keep this div in the center of the screen
  margin: "-190px 0 auto", // Center the component horizontally
  gap: "24px",
  zIndex: 2,
  padding: "0 16px",
  variants: {
    isSchedulingConsolidationEnabled: {
      true: {
        maxWidth: "970px",
        gap: "30px",
        padding: "0 20px",
      },
    },
    isMobile: {
      true: {
        gap: "16px",
        margin: "0 auto",
        padding: "24px 20px 0",
      },
    },
  },
});

const FindProviderWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  position: "relative",
  width: "100%",
});

const IllustrationWrapper = styled("div", {
  width: "100%",
  maxWidth: "1400px",
});

const ProviderListCenterWrapper = styled("div", {
  display: "flex",
  alignSelf: "center",
  justifyContent: "center",
  flexDirection: "column",
  width: "100%",
});

export const FindProvider = ({
  isBrowseIntakePending = false,
  isCombinedInitialSearchPending = false,
  onCombinedInitialSearchComplete,
}: {
  /**
   * The combined treatment keeps the standard Find Provider surface visible
   * behind its ZIP intake dialog, but must not search before a location exists.
   */
  isBrowseIntakePending?: boolean;
  /** Retains the POC skeletons until the treatment's first search resolves. */
  isCombinedInitialSearchPending?: boolean;
  onCombinedInitialSearchComplete?: () => void;
} = {}) => {
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const isSmallerTablet = useMediaQuery(MEDIA_QUERIES.smallerTabletMax);
  const isCompactAvailabilityViewport = useMediaQuery(MEDIA_QUERIES.compactAvailabilityMax);
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { data, setData } = useApplicationContext();
  const { FEATURE_SCHEDULING_CONSOLIDATION } = useFeatureFlags();
  const isSchedulingConsolidationEnabled = Boolean(FEATURE_SCHEDULING_CONSOLIDATION);
  // Treatment controls retain their POC button presentation after ZIP submit;
  // only the initial search itself is blocked until a trusted address exists.
  const isCombinedFindProviderTreatment = Boolean(
    isBrowseIntakePending ||
      isCombinedInitialSearchPending ||
      data?.isCombinedFindProviderTreatment,
  );
  useEffect(() => {
    logger.info({
      level: "info",
      text: "Clearing selectedDate and selectedDepartment in ApplicationContext",
      location: "FindProvider",
    });
    setData("selectedDate", null);
    setData("selectedDepartment", null);
  }, [setData]);
  return (
    <OverlayProvider>
      <ProviderListProvider>
        <FilterProvider>
          <Router.MetaTags title="Find Provider" />
          <FindProviderWrapper>
            <Filters isBrowseIntakePending={isCombinedFindProviderTreatment} />
            <PageLayout>
              <TopBGView isMobile={isMobile}>
                {/* Below sits within TopBGView div */}
                {!isTablet && (
                  <IllustrationWrapper>
                    <ThemedIllustration
                      themeIllustrationName="telescope"
                      altText="Telescope illustration"
                      brandProps={{
                        illustration: "telescope",
                        size: "183.78px",
                      }}
                    />
                  </IllustrationWrapper>
                )}
              </TopBGView>
              <ProviderListCenterWrapper>
                {/* Display View sits on top of PageLayout and TopBGView */}
                <DisplayView
                  data-testid="test-id-display-view"
                  isMobile={isMobile}
                  isSchedulingConsolidationEnabled={isSchedulingConsolidationEnabled}
                >
                  <TestRecordBanner visible={isTestRecordBlocked(data)} />
                  <ProviderList
                    isMobile={isMobile}
                    isTablet={isTablet}
                    isSmallerTablet={isSmallerTablet}
                    isCompactSlotControls={isCompactAvailabilityViewport || isMobile}
                    isBrowseIntakePending={isBrowseIntakePending}
                    isCombinedInitialSearchPending={isCombinedInitialSearchPending}
                    onCombinedInitialSearchComplete={onCombinedInitialSearchComplete}
                  />
                </DisplayView>
              </ProviderListCenterWrapper>
            </PageLayout>
          </FindProviderWrapper>
        </FilterProvider>
      </ProviderListProvider>
    </OverlayProvider>
  );
};
